import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Users, Stethoscope, Calculator, FileText, Briefcase, Sparkles, LogOut, Leaf, Menu, X, Radar, GraduationCap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Copilot from "./Copilot";
import { DigestBanner } from "./academy/WeeklyDigest";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Control Tower", icon: LayoutDashboard, end: true },
  { to: "/pipeline", label: "CRM & Pipeline", icon: KanbanSquare },
  { to: "/finder", label: "Opportunity Finder", icon: Radar },
  { to: "/contacts", label: "Organisations", icon: Users },
  { to: "/diagnostic", label: "AI Diagnostic", icon: Stethoscope },
  { to: "/pricing", label: "Pricing", icon: Calculator },
  { to: "/proposals", label: "Proposals", icon: FileText },
  { to: "/engagements", label: "Engagements", icon: Briefcase },
  { to: "/academy", label: "Adviser Academy", icon: GraduationCap },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [copilot, setCopilot] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");

  const SidebarInner = () => (
    <>
      <div className="px-5 py-5 flex items-center gap-2.5 border-b border-slate-800/60">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
          <Leaf className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="font-display font-bold text-white text-sm leading-tight">SusTek</div>
          <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Adviser OS</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMobileNav(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
              isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-white hover:bg-slate-800/60"
            )}>
            <item.icon className="w-[18px] h-[18px]" /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-3 border-t border-slate-800/60">
        <button onClick={() => { setCopilot(true); setMobileNav(false); }} data-testid="open-copilot"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 transition-colors shadow-lg shadow-emerald-900/30">
          <Sparkles className="w-[18px] h-[18px]" /> AI Copilot
        </button>
      </div>
      <div className="px-3 py-3 border-t border-slate-800/60">
        <div className="flex items-center gap-3 px-2 py-2">
          <button onClick={() => { nav("/profile"); setMobileNav(false); }} data-testid="profile-link" className="flex items-center gap-3 flex-1 min-w-0 text-left group">
            <div className="w-9 h-9 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-semibold group-hover:bg-emerald-600 transition-colors">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium truncate group-hover:text-emerald-300 transition-colors">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate capitalize">{(user?.role || "").replace("_", " ")}</div>
            </div>
          </button>
          <button onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button"
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-slate-900 fixed inset-y-0 left-0 z-30">
        <SidebarInner />
      </aside>

      {/* Mobile nav */}
      {mobileNav && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64 bg-slate-900 flex flex-col" data-testid="mobile-sidebar"><SidebarInner /></div>
          <div className="flex-1 bg-slate-900/50" onClick={() => setMobileNav(false)} />
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileNav(true)} data-testid="mobile-menu-button" className="p-2 -ml-2 text-slate-700"><Menu className="w-5 h-5" /></button>
          <div className="font-display font-bold text-slate-900">SusTek</div>
          <button onClick={() => setCopilot(true)} className="p-2 text-emerald-600"><Sparkles className="w-5 h-5" /></button>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-8 max-w-[1400px] w-full mx-auto">
          <DigestBanner className="mb-6" />
          {children}
        </main>
      </div>

      <Copilot open={copilot} onClose={() => setCopilot(false)} context={loc.pathname} />
    </div>
  );
}
